import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MonitoredFlow } from '../../../model/client/CORE';
import Utils from '../../../common/util/Utils';
import { RootState } from 'app/redux/store';

const CUSTOM_FLOW_LS_KEY = 'customFlows';
const commonFlows = [
    new MonitoredFlow(0, 'FLOW UK CANARIA', ['BIRDES', 'EETTALL', 'LIMADE'], true),
    new MonitoredFlow(1, 'FLOW SOUTH EAST AXIS', ['LFEKE', 'LFEKR', 'LFMAA', 'LIRUS', 'UKOVT'], true),
];

export interface UserMonitoredFlowsSectionState {
    flows: MonitoredFlow[];
}

const loadInitialState = () => {
    try {
        // Restore custom flows from the local storage
        if (!localStorage.getItem(CUSTOM_FLOW_LS_KEY)) return [...commonFlows];

        const flows = [...commonFlows];

        const customFlows: Array<MonitoredFlow> = JSON.parse(localStorage.getItem(CUSTOM_FLOW_LS_KEY));
        const additionalTVs = [];
        customFlows.forEach(customFlow => {
            Utils.addAllIfNeeded(additionalTVs, customFlow.tvIds);
            flows.splice(flows.length, 0, customFlow);
        });

        return flows;
    } catch (err) {
        console.error('Failed to parse localStorage data:', err);
        return [...commonFlows];
    }
};
const initialState: UserMonitoredFlowsSectionState = {
    flows: loadInitialState(),
};

const updateFlows = (state: UserMonitoredFlowsSectionState, flows: Array<MonitoredFlow>) => {
    state.flows = flows;
    try {
        localStorage.setItem(CUSTOM_FLOW_LS_KEY, JSON.stringify(flows.filter?.(flow => !flow.predefined)));
    } catch (err) {
        console.error('Failed to save localStorage data:', err);
    }
};

const displaceFlowSection = (state: UserMonitoredFlowsSectionState, beforeIdx: number, afterIdx: number) => {
    if (afterIdx < 0) return;
    const newFlows = state.flows.slice();
    const sections = newFlows.splice(beforeIdx, 1);
    newFlows.splice(afterIdx, 0, sections[0]);
    updateFlows(state, newFlows);
};

const userMonitoredFlowsSlice = createSlice({
    name: 'user-monitored-flows',
    initialState: initialState,
    reducers: {
        createFlowSection: (
            state,
            action: PayloadAction<{ idx?: number; section?: MonitoredFlow; tvIds?: string[] }>,
        ) => {
            const uuid = Math.max(...state.flows.map(flow => flow.uuid)) + 1;
            const customSection = action.payload.section || new MonitoredFlow(uuid, 'CUSTOM SECTION', []);
            if (action.payload.tvIds) {
                customSection.tvIds = action.payload.tvIds;
            }
            const idx = action.payload.idx ?? state.flows.length;
            const newFlows = state.flows.slice();
            newFlows.splice(idx, 0, customSection);
            updateFlows(state, newFlows);
        },
        moveFlowSectionUp: (state, action: PayloadAction<number>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload);
            displaceFlowSection(state, idx, idx - 1);
        },
        moveFlowSectionDown: (state, action: PayloadAction<number>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload);
            displaceFlowSection(state, idx, idx + 1);
        },
        removeFromFlowSection: (state, action: PayloadAction<{ flow: MonitoredFlow; tvsToRemove: Array<string> }>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload.flow.uuid);
            const newFlows = state.flows.slice();
            const flow = newFlows[idx];
            flow.tvIds = Utils.copyAndRemoveAllIfNeeded(flow.tvIds, action.payload.tvsToRemove);
            updateFlows(state, newFlows);
        },
        addToFlowSection: (state, action: PayloadAction<{ flow: MonitoredFlow; tvsToAdd: Array<string> }>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload.flow.uuid);
            const newFlows = state.flows.slice();
            const flow = newFlows[idx];
            flow.tvIds = Utils.copyAndAddAllIfNeeded(flow.tvIds, action.payload.tvsToAdd);
            updateFlows(state, newFlows);
        },
        changeFlowsSectionTitle: (state, action: PayloadAction<{ flowUuid: number; newTitle: string }>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload.flowUuid);
            const flow = state.flows[idx];
            flow.flowName = action.payload.newTitle;
            const newFlows = state.flows.slice();
            newFlows.splice(idx, 1, flow);
            updateFlows(state, newFlows);
        },
        deleteFlowSection: (state, action: PayloadAction<number>) => {
            const idx = state.flows.findIndex(flow => flow.uuid === action.payload);
            const newFlows = state.flows.slice();
            newFlows.splice(idx, 1);
            updateFlows(state, newFlows);
        },
    },
});

export default userMonitoredFlowsSlice.reducer;
export const {
    createFlowSection,
    changeFlowsSectionTitle,
    moveFlowSectionUp,
    moveFlowSectionDown,
    deleteFlowSection,
    removeFromFlowSection,
    addToFlowSection,
} = userMonitoredFlowsSlice.actions;

export const selectAllFlows = (state: RootState) => state?.userMonitoredFlowsState?.flows ?? [];
export const selectUserCustomFlows = createSelector([selectAllFlows], flows => flows.filter(flow => !flow.predefined));
